import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SimulatorService } from '../../../pre-simulator/services/simulator/simulator.service';
import { TaxesComponent } from '../../../shared/components/taxes/taxes.component';
import { GoogleTagManagerModel } from '../../../shared/models/gtm.models';
import { UserDataModel } from '../../../shared/models/user-data.model';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { GtmService } from '../../../shared/services/gtm/gtm.service';
import { UserDataService } from '../../../shared/services/user-data/user-data.service';
import { MIN_STATUS } from '../../../shared/utils/constants';
import { ConfirmationService } from '../../../vass-core/components/modal/confirmation.service';
import { BeginReviewComponent } from '../../components/modals/begin-review/begin-review.component';
import { ResultSimulationL2 } from '../../models/result-simulationL2.model';
import { LayoutService } from '../../services/layout/layout.service';
import { MortgageService } from '../../services/mortgage/mortgage.service';

import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { RecoverUser } from '../../../shared/models/recoverUser.model';

@Component({
  selector: 'app-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss'],
  standalone: false,
})
export class ResultComponent implements OnInit, OnDestroy {
  public hasSimulated: boolean = false;
  public isSimulation: boolean;
  public dataSimulate: ResultSimulationL2;
  public personalData: UserDataModel;
  public httpError: boolean;
  public minAmount = 0;
  public totalExpenses = 0;

  private subscribe = new Subscription();

  collapseData = {
    commonData: true,
    firstHolder: true,
    secondHolder: true,
  };

  constructor(
    private confirmationService: ConfirmationService,
    private _userDataService: UserDataService,
    private _mortgageService: MortgageService,
    private _layoutService: LayoutService,
    private authService: AuthService,
    private translate: TranslateService,
    private simulatorService: SimulatorService,
    private gtm: GtmService,
  ) {}

  ngOnInit() {
    this.getResultSimulation();
    this.subscribe.add(
      this.authService
        .getStatus()
        .pipe(distinctUntilChanged())
        .subscribe((response) => {
          this.isSimulation =
            parseFloat(response) < MIN_STATUS.attachDocumentation &&
            parseFloat(response) !== MIN_STATUS.waitingAceptedSecondHolder;
          this.gtm.sendVarGTM({ pageType: 'resultados' });
        }),
    );
    this.subscribeUserDataChanges();
    this.minAmount = this.simulatorService.MORTGAGE_AMOUNT_MIN;
  }

  public getResultSimulation() {
    this.dataSimulate = this._userDataService.resultSimulate;
    this.personalData = this._userDataService.getUserData();
    this._mortgageService.simulateLevel2(false).subscribe(
      ({ mortgageSimulateResponse }) => {
        this.dataSimulate = mortgageSimulateResponse;
      },
      (err) => {
        this.httpError = true;
      },
    );
  }

  private subscribeUserDataChanges() {
    this._layoutService.recoverUserData().subscribe(
      (userData: RecoverUser) => {
        this._userDataService.mapperUserData(userData);
        this.translateLabels();
      },
      (error) => {},
    );
  }

  private translateLabels() {
    this.personalData.level2Data.holders.forEach((holder) => {
      holder.nationality = this.simulatorService.findCountry(holder.nationality);
      holder.country = this.simulatorService.findCountry(holder.country);
    });

    this.personalData.level1Data.mortgageAmount = this.mortgageAmount;
    this.totalExpenses = this.calculateExpenses();
  }

  public calculateExpenses() {
    if (this.dataSimulate && this.dataSimulate.mortgageResult) {
      return (
        parseFloat(this.dataSimulate.mortgageResult.expenses.mortgage.total) +
        parseFloat(this.dataSimulate.mortgageResult.expenses.trading.total)
      );
    }
    return 0;
  }

  public openModal(type: string) {
    let modalComponent: Object;
    let titleModal: string;
    const data: Object = {
      savingsMade: this._userDataService.getUserData().level1Data.savingsMade,
      dataSimulate: this.dataSimulate.mortgageResult,
    };
    switch (type) {
      case 'taxes': {
        titleModal = this.translate.instant('result_taxes');
        modalComponent = TaxesComponent;
        break;
      }
      case 'beginReview': {
        titleModal = this.translate.instant('simulation_result_title');
        modalComponent = BeginReviewComponent;
        this.gtm.sendEventGTM({
          event: 'interaccion',
          eAction: 'recogida_de_datos',
          eLabel: 'confirmacion',
        });
        break;
      }
    }
    const options: Object = {
      showHeader: true,
      header: titleModal,
      closeOnEscape: true,
      closable: true,
      styleClass: 'modal-class',
      cancelButton: false,
      acceptButton: false,
    };

    this.confirmationService.confirm(<any>{
      component: modalComponent,
      options: options,
      data: data,
      accept: (data) => {},
      reject: () => {},
    });
  }

  public collapse(item: string) {
    this.collapseData[item] = !this.collapseData[item];
  }

  public collapseHolder(item) {
    item.isVisible = !item.isVisible;
  }

  public get mortgageAmount() {
    return String(
      typeof this.personalData.level1Data.mortgageAmount === 'number'
        ? this.personalData.level1Data.mortgageAmount
        : parseFloat(
            this.personalData.level1Data.mortgageAmount.toString().split('.').join(''),
          ),
    );
  }

  public sendEventGTM(event: GoogleTagManagerModel) {
    this.gtm.sendEventGTM(event);
  }

  ngOnDestroy() {
    this.subscribe.unsubscribe();
  }
}
